import { t } from 'i18next';
import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { IRowProps } from '../../../../../../shared/frontend/components/grid/Row';
import { DetailedPatreonPlug } from '../../../../../../shared/frontend/components/patreon/detailedPatreonPlug';
import { useKnownUserSubscriptions } from '../../../../../../shared/frontend/components/subscriptions/useKnownUserSubscriptions';
import {
  TXsollaStartCheckoutFn,
  XsollaSubscriptionOptions
} from '../../../../../../shared/frontend/components/subscriptions/xsollaSubscriptionOptions';
import { PatreonCheckoutLink } from '../../../../../../shared/frontend/components/subscriptions/patreonCheckoutLink';
import {
  TPremiumCheckoutSurface,
  TPremiumRailPosition
} from '../../../../../../shared/frontend/components/subscriptions/premiumXsollaAnalytics';
import { TXsollaCheckoutSelection } from '../../../../../../shared/frontend/components/subscriptions/useXsollaCheckoutSession';
import { useXsollaPremiumFlow } from '../../../../../../shared/frontend/components/subscriptions/useXsollaPremiumFlow';
import { getActiveXsollaPlanState } from '../../../../../../shared/frontend/components/subscriptions/xsollaActivePlan';
import { navigateToWithPageRefresh } from '../../../../../../shared/frontend/libs/history';
import { XSOLLA_ACTIVE_SUBSCRIPTION_ERROR_CODE } from '../../../../../../shared/common/xsolla';
import { toXsollaCheckoutError } from '../../../../../../shared/common/xsollaCheckoutError';
import { TXsollaCheckoutExperience } from '../../../../../../shared/common/xsollaRollout';
import { buildPremiumCheckoutSuccessPath } from '../../libs/premiumCheckoutSuccess';
import { apiServiceAsync } from '../../services/apiService';
import { isXsollaAuthenticationError, xsollaCheckoutClient } from '../../services/xsollaCheckoutClient';

type TPremiumSubscriptionRailProps = {
  checkoutConfig: TXsollaCheckoutExperience;
  isAuthenticated: boolean;
  onRequireAuthentication: () => void;
  subscriptionState?: ReturnType<typeof useKnownUserSubscriptions>;
  checkoutSurface?: TPremiumCheckoutSurface;
  showCheckoutFooter?: boolean;
  /** Layout pass-through, applied to whichever provider's cards are rendered. */
  className?: string;
  rowProps?: IRowProps;
  /**
   * Which of the two identical /premium rails this is, so plan impressions and
   * checkout events can be attributed to the above-the-fold rail vs the one
   * after the full-page read.
   */
  railPosition?: TPremiumRailPosition;
};

export function PremiumSubscriptionRail(props: TPremiumSubscriptionRailProps) {
  const {
    checkoutConfig,
    checkoutSurface = 'premium_page',
    className,
    isAuthenticated,
    onRequireAuthentication,
    railPosition,
    rowProps,
    subscriptionState,
    showCheckoutFooter = true
  } = props;
  const location = useLocation();
  const loadedSubscriptionState = useKnownUserSubscriptions({
    enabled: !subscriptionState && (checkoutConfig.isVisible || isAuthenticated),
    isAuthenticated,
    loadSubscriptions: apiServiceAsync.getAllSubscriptions
  });
  const { subscriptions, connectedAccounts, status, refreshSubscriptions } =
    subscriptionState || loadedSubscriptionState;
  const hasActiveLocalXsollaPlan = isAuthenticated && !!getActiveXsollaPlanState(subscriptions);
  const xsollaFlowActive = checkoutConfig.isVisible || hasActiveLocalXsollaPlan;
  const effectiveCheckoutConfig =
    !checkoutConfig.isVisible && hasActiveLocalXsollaPlan
      ? { ...checkoutConfig, isVisible: true }
      : checkoutConfig;
  const checkoutStarterRef = useRef<TXsollaStartCheckoutFn | null>(null);
  const resumeCheckoutFromNotice = useCallback(
    (selection: TXsollaCheckoutSelection) =>
      checkoutStarterRef.current?.(selection.tier, selection.isAnnual, selection.operation),
    []
  );
  const {
    pricing,
    pricingInfo,
    isPricingLoading,
    isPricingUnavailable,
    checkoutPhase,
    checkoutNotice,
    checkoutNoticeActionLabel,
    checkoutNoticeActionPath,
    checkoutNoticeActionRun,
    clearCheckoutFeedback,
    onCheckoutClose,
    onCheckoutError,
    onCheckoutPending,
    onCheckoutStart,
    onCheckoutSuccess
  } = useXsollaPremiumFlow({
    enabled: xsollaFlowActive,
    market: checkoutConfig.marketCountry || undefined,
    fetchPricing: apiServiceAsync.getXsollaPricing,
    navigateToSuccessPage: (selection) =>
      navigateToWithPageRefresh(
        buildPremiumCheckoutSuccessPath(location.pathname, {
          tier: selection.tier,
          billingPeriod: selection.isAnnual ? 'annual' : 'monthly',
          operation: selection.operation
        })
      ),
    resumeCheckout: resumeCheckoutFromNotice
  });
  const hasConnectedXsollaAccount = connectedAccounts.some(
    (connectedAccount) => connectedAccount.provider === 'xsolla' && connectedAccount.isConnected
  );

  const runActivationSync = useCallback(async () => {
    try {
      const result = await xsollaCheckoutClient.getActivationStatus({ attemptRecovery: true });

      if (result.hasActiveSubscription || result.xsollaAccountConnected || result.recovered > 0) {
        await refreshSubscriptions();
      }
    } catch (error) {
      if (isXsollaAuthenticationError(error)) {
        onRequireAuthentication();
        return;
      }

      console.warn('Failed to sync Xsolla activation status:', error);
    }
  }, [onRequireAuthentication, refreshSubscriptions]);

  useEffect(() => {
    if (!xsollaFlowActive || status !== 'ready' || !hasConnectedXsollaAccount || hasActiveLocalXsollaPlan) {
      return;
    }

    void runActivationSync();
  }, [xsollaFlowActive, hasActiveLocalXsollaPlan, hasConnectedXsollaAccount, runActivationSync, status]);

  if (!checkoutConfig.isVisible && isAuthenticated && status !== 'ready') {
    return null;
  }

  // Xsolla isn't available for this viewer: the Patreon plug is the only option, so no toggle.
  if (!xsollaFlowActive) {
    return (
      <DetailedPatreonPlug
        premiumPage={false}
        buttonTextVariants="unique"
        analyticsSurface={checkoutSurface}
        marketCountry={checkoutConfig.marketCountry}
        railPosition={railPosition}
        className={className}
        rowProps={rowProps}
      />
    );
  }

  const isCheckoutError = checkoutPhase === 'error';
  const isSubscriptionStateInitializing = status === 'loading';
  const disableCheckout = isSubscriptionStateInitializing || status === 'error' || isPricingUnavailable;
  const handleCheckoutError = (error: unknown) => {
    onCheckoutError(error as any);

    if (toXsollaCheckoutError(error)?.code === XSOLLA_ACTIVE_SUBSCRIPTION_ERROR_CODE) {
      void runActivationSync();
    }
  };

  return (
    <>
      {pricingInfo && (
        <div className="rio-alert-v2 rio-alert-v2--large rio-alert-v2--centered slds-m-bottom--medium" role="status">
          <div className="rio-alert-v2__icon">
            <i className="fa fa-info-circle" />
          </div>
          <div className="rio-alert-v2__content">
            <div className="rio-alert-v2__message">{pricingInfo}</div>
          </div>
        </div>
      )}
      {checkoutNotice && (
        <div
          className={`rio-alert-v2 rio-alert-v2--large rio-alert-v2--centered slds-m-bottom--medium${isCheckoutError ? ' rio-alert-v2--error' : ''}`}
          role={isCheckoutError ? 'alert' : 'status'}
        >
          <div className="rio-alert-v2__icon">
            <i className={`fa ${isCheckoutError ? 'fa-exclamation-triangle' : 'fa-info-circle'}`} />
          </div>
          <div className="rio-alert-v2__content">
            <div className="rio-alert-v2__message">{checkoutNotice}</div>
            {checkoutNoticeActionLabel && checkoutNoticeActionPath && (
              <div className="slds-m-top--small">
                <a href={checkoutNoticeActionPath} className="slds-button slds-button--neutral">
                  {checkoutNoticeActionLabel}
                </a>
              </div>
            )}
            {checkoutNoticeActionLabel && checkoutNoticeActionRun && (
              <div className="slds-m-top--small">
                <button
                  type="button"
                  className="slds-button slds-button--neutral"
                  onClick={checkoutNoticeActionRun}
                  disabled={disableCheckout}
                >
                  {checkoutNoticeActionLabel}
                </button>
              </div>
            )}
          </div>
          <button
            type="button"
            className="slds-button slds-button--icon-inverse slds-m-left--small"
            onClick={clearCheckoutFeedback}
            aria-label={t('common:terms.close')}
          >
            <i className="fa fa-times" />
          </button>
        </div>
      )}
      <XsollaSubscriptionOptions
        checkoutConfig={effectiveCheckoutConfig}
        checkoutSurface={checkoutSurface}
        showCheckoutFooter={showCheckoutFooter}
        railPosition={railPosition}
        className={className}
        rowProps={rowProps}
        isAuthenticated={isAuthenticated}
        pricing={pricing}
        isPricingLoading={isPricingLoading}
        isPricingUnavailable={isPricingUnavailable}
        isInitializing={isSubscriptionStateInitializing}
        disableCheckout={disableCheckout}
        subscriptions={subscriptions}
        manageSubscriptionPath="/settings/subscriptions"
        getCheckoutSuccessUrl={(tier, isAnnual, operation) =>
          buildPremiumCheckoutSuccessPath(location.pathname, {
            tier,
            billingPeriod: isAnnual ? 'annual' : 'monthly',
            operation: operation || undefined
          })
        }
        createPaymentToken={apiServiceAsync.createXsollaPaymentToken}
        onRequireAuthentication={onRequireAuthentication}
        onCheckoutStart={onCheckoutStart}
        onSuccess={onCheckoutSuccess}
        onPending={onCheckoutPending}
        onClose={onCheckoutClose}
        onError={handleCheckoutError}
        checkoutStarterRef={checkoutStarterRef}
      />
      {showCheckoutFooter && <PatreonCheckoutLink />}
    </>
  );
}
